import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaTimes } from 'react-icons/fa';
import { IP } from "../configuracion.js";
import "../styles/TransferTableSelector.css";
import { useProperty } from "../context/PropertyContext";

// TransferMeseroSelector
//
// Modal de la tecla "Transferencia de Mesero": reasigna el mesero
// responsable del cheque actualmente seleccionado, sin tocar mesa,
// totales, pedidos ni ningún otro dato de la cuenta.
//
// Reutiliza deliberadamente el mismo estilo, la misma forma de cargar
// employees.json y la misma estructura visual que TransferTableSelector.js
// (paso "vendor-selection" de ese componente) para no introducir un
// patrón nuevo — ver Requerimiento_Transferencia_Mesero.docx, sección 3.4
// ("reutilizar al máximo la lógica ya implementada").
const TransferMeseroSelector = ({
  currentCheque,
  currentUser,
  onTransferComplete,
  onClose,
}) => {
  const { property } = useProperty();
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadEmployees = async () => {
      if (!property) return;
      setEmployeesLoading(true);
      try {
        const response = await fetch(`${IP}/tavpos/backend/data/${property}/employees.json`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.empleados)
          ? data.empleados
          : [];
        setEmployees(list);
      } catch (err) {
        console.error("Error cargando empleados para transferencia de mesero:", err.message);
        setError("No se pudo cargar la lista de empleados.");
      } finally {
        setEmployeesLoading(false);
      }
    };
    loadEmployees();
  }, [property]);

  const handleConfirmTransfer = async () => {
    if (!selectedVendorId) {
      setError("Selecciona el mesero destino.");
      return;
    }
    if (!currentCheque?.numero_cheque) {
      setError("No hay cheque seleccionado.");
      return;
    }

    const vendorDestino = employees.find((emp) => String(emp.id) === String(selectedVendorId));
    if (!vendorDestino) {
      setError("El mesero seleccionado ya no está disponible.");
      return;
    }

    setTransferLoading(true);
    setError(null);
    try {
      const payload = {
        numero_cheque: currentCheque.numero_cheque,
        id_vendedor_nuevo: vendorDestino.id,
        nombre_vendedor_nuevo: vendorDestino.nombre,
        usuario: currentUser?.nombre || "Sistema",
      };

      const response = await fetch(
        `${IP}/tavpos/backend/transferir_mesero.php?property=${encodeURIComponent(property)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Error al transferir el mesero");
      }

      if (onTransferComplete) {
        onTransferComplete({
          success: true,
          message: `Mesero reasignado a ${vendorDestino.nombre}`,
          data: data.data,
        });
      }
      if (onClose) onClose();
    } catch (err) {
      console.error("[TransferMeseroSelector] Error:", err);
      setError(`⚠️ Error al transferir mesero: ${err.message}`);
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="transfer-table-selector">
      <div className="transfer-header">
        <h2><i className="bi bi-person-badge" style={{ marginRight: 8 }}></i>Transferencia de Mesero</h2>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      {currentCheque && (
        <div className="current-cheque-info">
          <span><strong>Mesa:</strong> {currentCheque.mesa}</span>
          <span><strong>Cheque #:</strong> {currentCheque.numero_cheque}</span>
          <span><strong>Área:</strong> {currentCheque.revenue}</span>
          {currentCheque.nombre_vendedor && (
            <span><strong>Mesero actual:</strong> {currentCheque.nombre_vendedor}</span>
          )}
        </div>
      )}

      {error && <div className="error">{error}</div>}
      {transferLoading && <div className="transfer-loading">Procesando transferencia...</div>}

      <div className="vendor-selection">
        <h3>Selecciona el mesero destino</h3>
        <p className="transfer-instruction">
          Esta acción solo cambia el mesero responsable de la cuenta. Mesa, pedidos y totales no se modifican.
        </p>

        <div className="revenue-list vendor-list">
          {employeesLoading && <p className="no-results">Cargando empleados...</p>}
          {!employeesLoading &&
            employees.map((emp) => (
              <button
                key={emp.id ?? emp.usr ?? emp.nombre}
                onClick={() => setSelectedVendorId(emp.id)}
                className={`revenue-button${String(selectedVendorId) === String(emp.id) ? " selected" : ""}`}
              >
                {emp.nombre}
              </button>
            ))}
          {!employeesLoading && employees.length === 0 && (
            <p className="no-results">No se pudo cargar la lista de empleados.</p>
          )}
        </div>

        <div className="action-buttons">
          <button className="cancel-button" onClick={onClose} disabled={transferLoading}>
            Cancelar
          </button>
          <button
            className="confirm-transfer-button"
            onClick={handleConfirmTransfer}
            disabled={transferLoading || !selectedVendorId}
          >
            {transferLoading ? "Transfiriendo..." : "Confirmar Transferencia"}
          </button>
        </div>
      </div>
    </div>
  );
};

TransferMeseroSelector.propTypes = {
  currentCheque: PropTypes.shape({
    numero_cheque: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mesa: PropTypes.string,
    revenue: PropTypes.string,
    nombre_vendedor: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.object,
  onTransferComplete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default TransferMeseroSelector;
